import type { MapBounds } from '../types';
// Grensedata deles med serveren. Fila ligger under functions/ fordi
// Firebase-deploy bare laster opp den mappa — klienten låner den herfra i stedet
// for at vi skal ha to kopier av 180 kB som kan drifte fra hverandre.
import GRENSER from '../functions/lib/nordic-borders.json';

export type SupportedCountry = 'NO' | 'SE' | 'DK' | 'FI';

/**
 * Landtilhørighet ut fra EKTE landegrenser (Natural Earth 1:10m, forenklet til ~1 km).
 *
 * VAR BOKSER FØR 29.08.2026, og rekkefølgen gjorde det verre: Danmarks boks
 * (lat 54,5–57,8 / lon 8,0–15,2) ble sjekket først, og den dekker hele Skåne,
 * Halland og halve Småland. Göteborg, Malmö, Jönköping, Halmstad og Växjö kom
 * derfor ut som DANMARK, mens Strömstad kom ut som NORGE fordi NO/SE-linja sa
 * lon 12,5 ved lat 59 der grensa i virkeligheten går ved ~11,7. Kartlagene
 * gates på landkoden (`isLayerSupportedInCountry`), så et svensk kart kunne få
 * danske lag og omvendt.
 *
 * SPEILING av functions/lib/nordicBorders.js. Klienten er TS/ESM, `functions/` er
 * CommonJS, og det finnes ikke noe felles byggesteg — samme situasjon som
 * utils/geocell.ts ↔ functions/lib/spotGeo.js. Bare ALGORITMEN er duplisert;
 * dataene er én fil. ENDRER DU NOE HER, endre samme sted i functions/lib/ —
 * `test/nordicBordersParity.test.ts` feiler ellers.
 */

interface Polygon {
  bbox: number[];      // [minLon, minLat, maxLon, maxLat]
  ring: number[][];    // [[lon, lat], ...]
}

const LAND: SupportedCountry[] = ['DK', 'SE', 'FI', 'NO'];
const GRENSE_KART = GRENSER as unknown as Record<SupportedCountry, Polygon[]>;

// Se functions/lib/nordicBorders.js for hvorfor taket er 25 km: Natural Earth er
// generalisert (Københavns havn ligger 1,6 km utenfor polygonet, Tromsøya 3,8 km),
// så kyst- og holmeplasser må fanges opp — men 2 300 plasser 25–60 km inne i
// Russland og Estland skal IKKE bli finske.
// Målt pris: 20 synlige plasser på Røst, Træna, Værøy og Halten faller utenfor,
// fordi Natural Earth 1:10m mangler de øyene. Et tak på 50 km ville hentet dem inn
// og samtidig gjort 1 916 russiske og tyske plasser nordiske.
const NAERMESTE_MAKS_KM = 25;
const INDEKS_CELLE = 0.5;
const KM_PER_BREDDEGRAD = 111.32;

/** Ray-casting punkt-i-polygon. ring = [[lon, lat], ...]. */
function pointInPolygon(lat: number, lon: number, ring: number[][]): boolean {
  let inne = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const krysser = ((yi > lat) !== (yj > lat))
      && (lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi);
    if (krysser) inne = !inne;
  }
  return inne;
}

// Rutenett-indeks over grensepunktene, så nærmeste-land-fallbacken slipper å måle
// mot alle ~9 800 punktene. Bygges én gang ved modullasting.
function byggIndeks(): Map<string, [number, number, SupportedCountry][]> {
  const indeks = new Map<string, [number, number, SupportedCountry][]>();
  for (const land of LAND) {
    for (const polygon of GRENSE_KART[land] || []) {
      for (const [lon, lat] of polygon.ring) {
        const nokkel = `${Math.floor(lat / INDEKS_CELLE)}_${Math.floor(lon / INDEKS_CELLE)}`;
        let bøtte = indeks.get(nokkel);
        if (!bøtte) indeks.set(nokkel, bøtte = []);
        bøtte.push([lon, lat, land]);
      }
    }
  }
  return indeks;
}
const PUNKT_INDEKS = byggIndeks();

/** Nærmeste land til et punkt utenfor alle polygoner, eller null utenfor taket. */
function naermesteLand(lat: number, lon: number, maksKm: number): SupportedCountry | null {
  const laBase = Math.floor(lat / INDEKS_CELLE);
  const loBase = Math.floor(lon / INDEKS_CELLE);
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const maksRinger = Math.ceil(maksKm / (INDEKS_CELLE * KM_PER_BREDDEGRAD)) + 1;
  let beste = Infinity;
  let besteLand: SupportedCountry | null = null;
  for (let r = 0; r <= maksRinger; r++) {
    for (let dla = -r; dla <= r; dla++) {
      for (let dlo = -r; dlo <= r; dlo++) {
        // Bare den nye ytterringen — de indre er allerede besøkt.
        if (Math.max(Math.abs(dla), Math.abs(dlo)) !== r) continue;
        const bøtte = PUNKT_INDEKS.get(`${laBase + dla}_${loBase + dlo}`);
        if (!bøtte) continue;
        for (const [plon, plat, land] of bøtte) {
          const d = Math.hypot((plon - lon) * cosLat * KM_PER_BREDDEGRAD, (plat - lat) * KM_PER_BREDDEGRAD);
          if (d < beste) { beste = d; besteLand = land; }
        }
      }
    }
    // Et treff er først endelig når vi har søkt én ring lenger ut enn treffets
    // avstand — ellers kan et nærmere punkt ligge i en celle vi ikke har åpnet.
    if (besteLand !== null && beste < r * INDEKS_CELLE * KM_PER_BREDDEGRAD) break;
  }
  return beste <= maksKm ? besteLand : null;
}

export function detectCountry(lat: number, lon: number): SupportedCountry | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  for (const land of LAND) {
    for (const polygon of GRENSE_KART[land] || []) {
      const bbox = polygon.bbox;
      if (lon < bbox[0] || lon > bbox[2] || lat < bbox[1] || lat > bbox[3]) continue;
      if (pointInPolygon(lat, lon, polygon.ring)) return land;
    }
  }
  return naermesteLand(lat, lon, NAERMESTE_MAKS_KM);
}

export function detectViewportCountry(bounds: MapBounds): SupportedCountry | null {
  const centerLat = (bounds.latMin + bounds.latMax) / 2;
  const centerLon = (bounds.lonMin + bounds.lonMax) / 2;
  return detectCountry(centerLat, centerLon);
}

export function isLayerSupportedInCountry(
  countrySupport: readonly string[],
  country: SupportedCountry,
): boolean {
  return countrySupport.includes('*') || countrySupport.includes(country);
}
